import { Avatar, ChoiceGroup, SegmentedControl } from '@shh/ui';
import { useState } from 'react';
import { useLens } from '../app/lens';
import { kids } from '../data/derive';
import { useFamily } from '../data/family';
import { formsText } from '../i18n/forms';
import { ShiftForm } from './ShiftForm';
import { TimetableForm } from './TimetableForm';

export type PlanKind = 'shift' | 'timetable';

/**
 * "Schicht- oder Stundenplan" (1.28, concept 24.10.): one entry, a switch on top. Picking a person
 * suggests the usual plan (a child → timetable, otherwise shifts); the person stays when switching.
 * Teens plan only for themselves (rules: own timetable, Mike 26.10.).
 */
export function PlanForm({ onDone, memberId }: { onDone: () => void; memberId?: string }) {
  const f = useFamily();
  const lens = useLens();
  const people = lens.canParent ? f.members : f.members.filter((m) => m.id === f.meId);
  const isChild = (id: string) => kids(f).some((m) => m.id === id);
  const [who, setWho] = useState(memberId ?? f.meId ?? people[0]?.id ?? '');
  const [kind, setKind] = useState<PlanKind>(isChild(who) ? 'timetable' : 'shift');
  const member = f.members.find((m) => m.id === who);
  return (
    <div className="flex flex-col gap-gap-lg">
      <SegmentedControl
        label={formsText.planKind}
        value={kind}
        onChange={setKind}
        options={[
          { value: 'shift', label: formsText.planShift },
          { value: 'timetable', label: formsText.planTimetable },
        ]}
        className="self-start max-sm:self-stretch"
      />
      {people.length > 1 && (
        <ChoiceGroup
          label={formsText.shiftFor}
          layout="person"
          value={who}
          onChange={(id) => {
            setWho(id);
            setKind(isChild(id) ? 'timetable' : 'shift');
          }}
          choices={people.map((m) => ({
            value: m.id,
            label: m.name,
            picture: <Avatar name={m.name} color={m.color} emoji={m.emoji} size="sm" decorative />,
          }))}
        />
      )}
      {kind === 'shift' && <ShiftForm key={who} fixedMemberId={who} onDone={onDone} />}
      {kind === 'timetable' && member && (
        <TimetableForm key={who} member={member} onDone={onDone} />
      )}
    </div>
  );
}
